import React, { useState, useEffect, useContext } from "react";
import { db } from "../../services/firebase";
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, where, updateDoc } from "firebase/firestore";
import { AuthContext } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import RifaGrid from "../../components/Rifa/RifaGrid";
import { useWindowSize } from "../../hooks/useWindowSize";
import { TOTAL_NUMBERS, TOTAL_PAGES } from "../../config";

const Dashboard = () => {
  const [soldNumbers, setSoldNumbers] = useState([]);
  const [pendingNumbersData, setPendingNumbersData] = useState([]); // Array de objetos {id, ...data}
  const [pendingNumbers, setPendingNumbers] = useState([]); // Array solo IDs para grid

  const [pendingUsers, setPendingUsers] = useState([]); // Usuarios pendientes de aprobación

  const [currentNumber, setCurrentNumber] = useState(null);
  const [numberData, setNumberData] = useState({});
  const [pageIndex, setPageIndex] = useState(0);

  const [vendedor, setVendedor] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  const [notification, setNotification] = useState({ message: '', type: '', show: false });

  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  // Escuchar 'vendidos'
  useEffect(() => {
    const collectionRef = collection(db, "vendidos");
    const unsubscribe = onSnapshot(collectionRef, (querySnapshot) => {
      const newData = {};
      const newSold = [];
      const newPending = [];
      const newPendingData = [];

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const num = parseInt(docSnap.id, 10);

        newData[num] = {
          nombre: data.nombre,
          telefono: data.telefono,
          vendedor: data.vendedor || "",
          status: data.status || 'approved' // status default
        };

        if (data.status === 'pending') {
          newPending.push(num);
          newPendingData.push({ id: num, ...data });
        } else {
          newSold.push(num);
        }
      });
      setNumberData(newData);
      setSoldNumbers(newSold);
      setPendingNumbers(newPending);
      setPendingNumbersData(newPendingData.sort((a, b) => a.id - b.id)); // Ordenar por número
    });
    return () => unsubscribe();
  }, []);

  // Escuchar usuarios pendientes
  useEffect(() => {
    const usersQuery = query(collection(db, "users"), where("status", "==", "pending"));
    const unsubscribe = onSnapshot(usersQuery, (querySnapshot) => {
      const users = [];
      querySnapshot.forEach((docSnap) => {
        users.push({ id: docSnap.id, ...docSnap.data() });
      });
      setPendingUsers(users.sort((a, b) => {
        // Ordenar por fecha de creación (más recientes primero)
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      }));
    });
    return () => unsubscribe();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, show: true });
    setTimeout(() => setNotification({ message: '', type: '', show: false }), 2000);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleNumberClick = (num) => {
    setCurrentNumber(num);
    const data = numberData[num];
    if (data) {
      setName(data.nombre);
      setPhone(data.telefono);
      setVendedor(data.vendedor || (user ? user.email : ""));

      // Debugging: mostrar información de propiedad
      console.log('=== NÚMERO SELECCIONADO ===');
      console.log('Número:', num);
      console.log('Usuario actual (user.email):', user?.email);
      console.log('Vendedor del documento:', data.vendedor);
      console.log('¿Es propietario?:', data.vendedor === user?.email);
    } else {
      setName("");
      setPhone("");
      setVendedor(user ? user.email : "");
    }
  };

  // Guardado manual (vendedor crea venta directa)
  const handleSaveOrUpdate = async (e) => {
    e.preventDefault();
    if (!name.trim() || !currentNumber) return;
    try {
      await setDoc(doc(db, "vendidos", currentNumber.toString()), {
        nombre: name,
        telefono: phone,
        vendedor: vendedor,
        status: 'approved' // Venta directa = aprobado
      });
      showNotification(`Venta guardada (N° ${currentNumber})`, 'success');
      setCurrentNumber(null); setName(""); setPhone("");
    } catch (error) {
      console.error('Error al guardar documento:', error);
      console.log('Usuario actual:', user?.email);
      console.log('Vendedor del documento:', numberData[currentNumber]?.vendedor);
      showNotification(`Error al guardar: ${error.message}`, 'error');
    }
  };

  // Acciones para Reservas Pendientes
  const approveReservation = async (numId) => {
    try {
      await updateDoc(doc(db, "vendidos", numId.toString()), {
        status: 'approved',
        vendedor: user.email // Registra quién aprobó
      });
      showNotification(`Reserva N° ${numId} aprobada`, 'success');
    } catch (error) {
      showNotification('Error al aprobar', 'error');
    }
  };

  const rejectReservation = async (numId) => {
    if (!window.confirm(`¿Rechazar y liberar el N° ${numId}?`)) return;
    try {
      await deleteDoc(doc(db, "vendidos", numId.toString()));
      showNotification(`Reserva N° ${numId} rechazada`, 'info');
    } catch (error) {
      showNotification('Error al rechazar', 'error');
    }
  };

  // Acciones para Usuarios Pendientes
  const approveUser = async (userId, userName) => {
    try {
      await updateDoc(doc(db, "users", userId), {
        status: 'approved'
      });
      showNotification(`Usuario ${userName} aprobado`, 'success');
    } catch (error) {
      console.error('Error al aprobar usuario:', error);
      showNotification('Error al aprobar usuario', 'error');
    }
  };

  const rejectUser = async (userId, userName) => {
    if (!window.confirm(`¿Rechazar y eliminar la solicitud de ${userName}?`)) return;
    try {
      await deleteDoc(doc(db, "users", userId));
      showNotification(`Solicitud de ${userName} rechazada`, 'info');
    } catch (error) {
      console.error('Error al rechazar usuario:', error);
      showNotification('Error al rechazar usuario', 'error');
    }
  };

  // Eliminar venta
  const handleDelete = async () => {
    if (!currentNumber || !window.confirm(`¿Liberar el ${currentNumber}?`)) return;
    try {
      console.log('Intentando eliminar número:', currentNumber);
      console.log('Usuario actual:', user?.email);
      console.log('Vendedor del documento:', numberData[currentNumber]?.vendedor);
      await deleteDoc(doc(db, "vendidos", currentNumber.toString()));
      showNotification(`Número ${currentNumber} liberado.`, 'success');
      setCurrentNumber(null); setName(""); setPhone("");
    } catch (error) {
      console.error('Error al eliminar:', error);
      showNotification(`Error al eliminar: ${error.message}`, 'error');
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark text-earthy-navy dark:text-white font-display">
      {/* Toast */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-[60] px-6 py-3 rounded-lg shadow-xl text-white font-bold ${notification.type === 'error' ? 'bg-red-600' : 'bg-primary'}`}>
          {notification.message}
        </div>
      )}

      <header className="bg-white dark:bg-earthy-navy border-b border-olive-drab/10 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4 sticky top-0 z-40">
        <h1 className="text-xl font-black uppercase tracking-tight flex items-center gap-2">
          <span className="material-symbols-outlined text-primary">dashboard</span>
          Panel de Control
        </h1>
        <div className="flex items-center gap-4 text-xs">
          <span>{user?.email}</span>
          <button onClick={handleLogout} className="bg-red-100 text-red-600 p-2 rounded-lg hover:bg-red-200">
            <span className="material-symbols-outlined">logout</span>
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-4 md:p-8 flex flex-col xl:flex-row gap-8">

        {/* Lado Izquierdo: Grilla */}
        <div className="flex-1 bg-white dark:bg-earthy-navy/40 p-6 rounded-2xl border border-olive-drab/10 shadow-sm">
          <RifaGrid
            soldNumbers={soldNumbers}
            pendingNumbers={pendingNumbers} // Mostrar amarillos
            currentNumber={currentNumber}
            onNumberClick={handleNumberClick}
            pageIndex={pageIndex}
          />

          <div className="flex justify-center mt-4 gap-4">
            <button className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded" onClick={() => setPageIndex(p => Math.max(0, p - 1))} disabled={pageIndex === 0}>◀</button>
            <span className="opacity-50 text-xs py-2">Página {pageIndex + 1} de {TOTAL_PAGES}</span>
            <button className="p-2 hover:bg-gray-100 dark:hover:bg-white/5 rounded" onClick={() => setPageIndex(p => Math.min(TOTAL_PAGES - 1, p + 1))} disabled={pageIndex === TOTAL_PAGES - 1}>▶</button>
          </div>
        </div>

        {/* Lado Derecho: Panel de Gestión */}
        <aside className="w-full xl:w-[400px] flex flex-col gap-6">

          {/* 1. SECCIÓN DE RESERVAS PENDIENTES */}
          {pendingNumbersData.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/10 p-6 rounded-2xl border border-yellow-200 dark:border-yellow-700/30">
              <h3 className="font-bold text-yellow-700 dark:text-yellow-500 mb-4 flex items-center gap-2">
                <span className="material-symbols-outlined">notifications_active</span>
                Reservas Pendientes ({pendingNumbersData.length})
              </h3>
              <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto">
                {pendingNumbersData.map((item) => (
                  <div key={item.id} className="bg-white dark:bg-earthy-navy p-3 rounded-lg shadow-sm border border-yellow-100 dark:border-white/5 flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded text-xs font-black">#{item.id}</span>
                        <span className="font-bold text-sm">{item.nombre}</span>
                      </div>
                      <div className="text-xs opacity-60 mt-0.5">{item.telefono}</div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => approveReservation(item.id)} className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200" title="Aprobar"><span className="material-symbols-outlined text-lg">check</span></button>
                      <button onClick={() => rejectReservation(item.id)} className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200" title="Rechazar"><span className="material-symbols-outlined text-lg">close</span></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 2. SECCIÓN DE USUARIOS PENDIENTES */}
          {pendingUsers.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/10 p-6 rounded-2xl border border-blue-200 dark:border-blue-700/30">
              <h3 className="font-bold text-blue-700 dark:text-blue-500 mb-4 flex items-center gap-2">
                <span className="material-symbols-outlined">person_add</span>
                Solicitudes de Acceso ({pendingUsers.length})
              </h3>
              <div className="flex flex-col gap-3 max-h-[300px] overflow-y-auto">
                {pendingUsers.map((user) => (
                  <div key={user.id} className="bg-white dark:bg-earthy-navy p-3 rounded-lg shadow-sm border border-blue-100 dark:border-white/5 flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="bg-blue-400 text-blue-900 px-2 py-0.5 rounded text-xs font-black">👤</span>
                        <span className="font-bold text-sm">{user.nombre}</span>
                      </div>
                      <div className="text-xs opacity-60 mt-0.5">{user.email}</div>
                      {user.createdAt && (
                        <div className="text-xs opacity-40 mt-0.5">
                          {user.createdAt.toDate().toLocaleDateString('es-ES', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => approveUser(user.id, user.nombre)} className="p-1.5 rounded bg-green-100 text-green-700 hover:bg-green-200" title="Aprobar Acceso"><span className="material-symbols-outlined text-lg">check</span></button>
                      <button onClick={() => rejectUser(user.id, user.nombre)} className="p-1.5 rounded bg-red-100 text-red-700 hover:bg-red-200" title="Rechazar Solicitud"><span className="material-symbols-outlined text-lg">close</span></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. FORMULARIO MANUAL */}
          <div className="bg-white dark:bg-earthy-navy/40 p-8 rounded-2xl border border-olive-drab/10 shadow-xl">
            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">edit_note</span>
              {currentNumber ? `Gestionar N° ${currentNumber}` : "Selecciona un número"}
            </h3>

            <form onSubmit={handleSaveOrUpdate} className="flex flex-col gap-4">
              <input type="text" className="w-full bg-background-light dark:bg-background-dark border-none rounded-lg p-3 text-sm" placeholder="Nombre Comprador" value={name} onChange={(e) => setName(e.target.value)} disabled={!currentNumber} />
              <input type="tel" className="w-full bg-background-light dark:bg-background-dark border-none rounded-lg p-3 text-sm" placeholder="Teléfono" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={!currentNumber} />
              <input type="text" className="w-full bg-background-light dark:bg-background-dark border-none rounded-lg p-3 text-sm" placeholder="Vendedor Responsable" value={vendedor} onChange={(e) => setVendedor(e.target.value)} disabled={!currentNumber} />

              {/* Indicador de propiedad */}
              {currentNumber && numberData[currentNumber] && (
                <div className="text-xs opacity-60 -mt-2">
                  {numberData[currentNumber].vendedor === user?.email ? (
                    <span className="text-green-600 dark:text-green-400">✓ Eres el vendedor de este número</span>
                  ) : (
                    <span className="text-orange-600 dark:text-orange-400">⚠ Vendido por: {numberData[currentNumber].vendedor}</span>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3 mt-4">
                {/* Botón de guardar/actualizar - solo si no existe o si es el propietario */}
                {(!currentNumber || !numberData[currentNumber] || numberData[currentNumber].vendedor === user?.email) && (
                  <button type="submit" disabled={!currentNumber || !name.trim()} className="col-span-2 bg-primary text-earthy-navy py-3 rounded-xl font-black uppercase tracking-wider hover:opacity-90 disabled:opacity-50">
                    {soldNumbers.includes(currentNumber) ? 'Actualizar Datos' : 'Registrar Venta Directa'}
                  </button>
                )}

                {/* Mensaje si no es el propietario */}
                {currentNumber && numberData[currentNumber] && numberData[currentNumber].vendedor !== user?.email && (
                  <div className="col-span-2 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-700/30 rounded-xl p-3 text-center text-xs text-orange-700 dark:text-orange-400">
                    🔒 Solo el vendedor propietario puede editar o eliminar esta venta
                  </div>
                )}

                {/* Botón de eliminar - solo si es el propietario */}
                {(soldNumbers.includes(currentNumber) || pendingNumbers.includes(currentNumber)) &&
                  numberData[currentNumber]?.vendedor === user?.email && (
                    <button type="button" onClick={handleDelete} className="col-span-2 bg-red-500/10 text-red-500 border border-red-500/20 py-2 rounded-xl font-bold uppercase text-xs hover:bg-red-500 hover:text-white transition-colors">
                      Liberar Número
                    </button>
                  )}
              </div>
            </form>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default Dashboard;